export const maxMediaFileSize = 5 * 1024 * 1024;
/** Límite generoso antes de comprimir (fotos de celular suelen pesar 6-15 MB). */
export const maxRawFileSize = 25 * 1024 * 1024;
export const allowedMediaTypes = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/svg+xml",
]);

export function safeMediaFileName(name: string): string {
  const extension = name.includes(".") ? name.split(".").pop() : "img";
  const base = name
    .replace(/\.[^.]+$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 70);
  return `${base || "imagen"}-${crypto.randomUUID()}.${extension}`;
}

export interface ImageVariantConfig {
  width: number;
  height: number;
  quality: number;
}

export interface ImageCompressionConfig {
  maxWidth: number;
  maxHeight: number;
  quality: number;
  webpQuality: number;
  maxFileSize: number;
  thumbnail: ImageVariantConfig;
  card: ImageVariantConfig;
  hero: ImageVariantConfig;
  gallery: ImageVariantConfig;
}

export const IMAGE_COMPRESSION_CONFIG: ImageCompressionConfig = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.82,
  webpQuality: 0.8,
  maxFileSize: maxMediaFileSize,
  thumbnail: { width: 400, height: 400, quality: 0.7 },
  card: { width: 640, height: 360, quality: 0.75 },
  hero: { width: 1280, height: 720, quality: 0.8 },
  gallery: { width: 800, height: 800, quality: 0.8 },
};

export type ImageSizeVariant = "thumbnail" | "card" | "hero" | "gallery";

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/") && allowedMediaTypes.has(file.type);
}

export function isWebPSupported(): boolean {
  if (typeof window === "undefined") return false;
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL("image/webp").startsWith("data:image/webp");
}

export function isAlreadyOptimized(file: File): boolean {
  if (!file.type.startsWith("image/")) return false;
  if (file.type === "image/webp") return true;
  if (file.size < 100 * 1024) return true;
  return false;
}

export function validateImageFile(
  file: File,
  options?: { sizeLimit?: number },
): { valid: boolean; error?: string } {
  if (!isImageFile(file)) {
    return { valid: false, error: "Solo se permiten imagenes JPG, PNG, WebP o GIF." };
  }
  const limit = options?.sizeLimit ?? maxMediaFileSize;
  if (file.size > limit) {
    return { valid: false, error: `La imagen no debe superar ${Math.round(limit / 1024 / 1024)} MB.` };
  }
  return { valid: true };
}

export interface CompressedImageResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  format: "webp" | "jpeg" | "png" | "gif";
  width: number;
  height: number;
  savingsPercent: number;
}

export async function compressImage(
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    forceWebP?: boolean;
  } = {},
): Promise<CompressedImageResult> {
  const {
    maxWidth = IMAGE_COMPRESSION_CONFIG.maxWidth,
    maxHeight = IMAGE_COMPRESSION_CONFIG.maxHeight,
    quality = IMAGE_COMPRESSION_CONFIG.quality,
    forceWebP = isWebPSupported(),
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      const aspectRatio = width / height;

      if (width > maxWidth || height > maxHeight) {
        if (width / maxWidth > height / maxHeight) {
          width = maxWidth;
          height = Math.round(width / aspectRatio);
        } else {
          height = maxHeight;
          width = Math.round(height * aspectRatio);
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d", { willReadFrequently: false });
      if (!ctx) {
        reject(new Error("No se pudo crear el contexto del canvas"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      const outputFormat = forceWebP && file.type !== "image/gif" ? "image/webp" : file.type;
      const outputQuality = outputFormat === "image/webp" ? IMAGE_COMPRESSION_CONFIG.webpQuality : quality;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Error al comprimir la imagen"));
            return;
          }

          const extension = outputFormat === "image/webp" ? "webp" : file.name.split(".").pop() ?? "jpg";
          const baseName = file.name.replace(/\.[^.]+$/, "");
          const compressedFile = new File([blob], `${safeMediaFileName(baseName)}.${extension}`, {
            type: outputFormat,
            lastModified: Date.now(),
          });

          resolve({
            file: compressedFile,
            originalSize: file.size,
            compressedSize: blob.size,
            format: outputFormat === "image/webp" ? "webp" : file.type.split("/")[1] as "jpeg" | "png" | "gif",
            width,
            height,
            savingsPercent: Math.round((1 - blob.size / file.size) * 100),
          });
        },
        outputFormat,
        outputQuality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("No se pudo cargar la imagen para compresion"));
    };

    img.src = objectUrl;
  });
}

export async function compressImageToVariants(
  file: File,
  variants: ImageSizeVariant[] = ["hero", "card", "thumbnail"],
): Promise<Record<ImageSizeVariant, CompressedImageResult>> {
  const results: Partial<Record<ImageSizeVariant, CompressedImageResult>> = {};

  for (const variant of variants) {
    const config = IMAGE_COMPRESSION_CONFIG[variant];
    results[variant] = await compressImage(file, {
      maxWidth: config.width,
      maxHeight: config.height,
      quality: config.quality,
    });
  }

  return results as Record<ImageSizeVariant, CompressedImageResult>;
}

export function createImagePreview(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("No es un archivo de imagen"));
      return;
    }
    const url = URL.createObjectURL(file);
    resolve(url);
  });
}

export function revokeImagePreview(url: string): void {
  URL.revokeObjectURL(url);
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export async function uploadImageWithProgress(
  file: File,
  uploadFn: (file: File) => Promise<string>,
  _onProgress?: (progress: UploadProgress) => void,
): Promise<string> {
  return uploadFn(file);
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}